import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Clock,
  HardHat,
  CheckCircle2,
  ArrowRight,
  Loader2,
  CalendarClock,
} from "lucide-react";
import { api } from "../../lib/api";

// Empty payload — used both as the initial state and as the fallback
// when the API call fails so the widget always renders something.
const EMPTY = {
  counts: { open_total: 0, overdue: 0, due_soon: 0, no_deadline: 0, completed: 0 },
  overdue: [],
  due_soon: [],
};

function CountPill({ label, value, tone, icon: Icon }) {
  const toneClasses = {
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    zinc: "bg-white border-zinc-200 text-zinc-900",
  };
  return (
    <div
      data-testid={`myresp-pill-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`rounded-xl border p-3 ${toneClasses[tone] || toneClasses.zinc}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider opacity-70">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-0.5 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function ListRow({ item, tone }) {
  const rightLabel =
    tone === "rose"
      ? `${item.days_overdue} gün gecikti`
      : item.days_left === 0
        ? "Bugün"
        : `${item.days_left} gün`;
  const chipTone =
    tone === "rose"
      ? "bg-rose-100 text-rose-800"
      : item.days_left <= 3
        ? "bg-amber-100 text-amber-800"
        : "bg-zinc-100 text-zinc-700";
  const stand = item.stand_no ? ` #${item.stand_no}` : "";
  return (
    <Link
      to={`/app/cop/construction-tracking/${item.construction_id}`}
      data-testid={`myresp-row-${item.construction_id}-${item.key}`}
      className="group flex items-center gap-2 rounded-lg px-2 py-1.5 text-[12px] transition-colors hover:bg-white"
    >
      <span
        className={`inline-flex h-1.5 w-1.5 shrink-0 rounded-full ${tone === "rose" ? "bg-rose-500" : "bg-amber-500"}`}
      />
      <span className="min-w-0 flex-1 truncate text-zinc-900">
        <span className="text-zinc-500">
          {item.company_name || "—"}
          {stand} · {item.group_label} ·
        </span>{" "}
        {item.label}
      </span>
      <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${chipTone}`}>
        {rightLabel}
      </span>
      <ArrowRight className="h-3 w-3 shrink-0 text-zinc-400 opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
}

export default function MyResponsibilitiesCard() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get("/construction-stats/my-responsibilities");
        if (mounted) setData(res.data || EMPTY);
      } catch {
        if (mounted) setData(EMPTY);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const c = data.counts;
  // Hide the whole widget when the user has zero involvement — avoids
  // showing an empty card that adds noise for people not on any
  // construction row (e.g. platform admins).
  if (!loading && c.open_total === 0 && c.completed === 0) return null;

  return (
    <section
      data-testid="my-responsibilities-widget"
      className="rounded-2xl border border-zinc-200 bg-gradient-to-br from-white to-zinc-50 p-5"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-100 to-sky-100">
            <HardHat className="h-4 w-4 text-violet-700" />
          </div>
          <div>
            <h2 className="text-[15px] font-semibold text-zinc-900">
              Bekleyen Sorumluluklarım
            </h2>
            <p className="text-[11px] text-zinc-500">
              Sana atanmış inşaat aşamalarının deadline özeti.
            </p>
          </div>
        </div>
        <Link
          to="/app/cop/construction-tracking"
          data-testid="myresp-open-all"
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:text-violet-900"
        >
          Tümünü gör <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <CountPill
              label="Gecikmiş"
              value={c.overdue}
              tone={c.overdue > 0 ? "rose" : "zinc"}
              icon={AlertTriangle}
            />
            <CountPill
              label="Bu hafta"
              value={c.due_soon}
              tone={c.due_soon > 0 ? "amber" : "zinc"}
              icon={Clock}
            />
            <CountPill
              label="Tarihsiz"
              value={c.no_deadline}
              tone="zinc"
              icon={CalendarClock}
            />
            <CountPill
              label="Tamamlanan"
              value={c.completed}
              tone="emerald"
              icon={CheckCircle2}
            />
          </div>

          {(data.overdue.length > 0 || data.due_soon.length > 0) && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {data.overdue.length > 0 && (
                <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-3">
                  <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-rose-900">
                    <AlertTriangle className="h-3.5 w-3.5" /> Gecikmiş
                    <span className="ml-auto rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-800">
                      {c.overdue}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {data.overdue.map((it) => (
                      <ListRow key={`${it.construction_id}-${it.key}`} item={it} tone="rose" />
                    ))}
                    {c.overdue > data.overdue.length && (
                      <div className="pl-3.5 pt-1 text-[10px] italic text-rose-700">
                        +{c.overdue - data.overdue.length} tane daha
                      </div>
                    )}
                  </div>
                </div>
              )}
              {data.due_soon.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3">
                  <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-amber-900">
                    <Clock className="h-3.5 w-3.5" /> Yakında dolacak (7 gün)
                    <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                      {c.due_soon}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {data.due_soon.map((it) => (
                      <ListRow key={`${it.construction_id}-${it.key}`} item={it} tone="amber" />
                    ))}
                    {c.due_soon > data.due_soon.length && (
                      <div className="pl-3.5 pt-1 text-[10px] italic text-amber-700">
                        +{c.due_soon - data.due_soon.length} tane daha
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {c.open_total > 0 && data.overdue.length === 0 && data.due_soon.length === 0 && (
            <div className="mt-4 rounded-xl border border-dashed border-zinc-200 bg-white p-4 text-center text-[12px] text-zinc-500">
              <CheckCircle2 className="mx-auto mb-1 h-5 w-5 text-emerald-500" />
              Yaklaşan deadline yok — {c.open_total} aşama planlı, {c.no_deadline}&apos;i tarihsiz.
            </div>
          )}
        </>
      )}
    </section>
  );
}
